import { Button } from "reactstrap";
import PrintSlipTemplate from "./printSlipTemplate";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { createSelector } from "reselect";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useMemo, useRef, useState } from "react";

import {
  getPaySlipList as onGetPaySlipList,
  loadEmployeePaySlipDetails as onLoadEmployeePaySlipDetails,
} from "../../../slice/thunks";
import { useReactToPrint } from "react-to-print";

/* =========================
   Selector (OUTSIDE component)
========================= */
const selectPayroll = (state) => state.Payroll;

const payRollSelector = createSelector(selectPayroll, (payroll) => ({
  paySlipList: payroll?.paySlipList || [],
  employeePaySlipDetails: payroll?.employeePaySlipDetails || null,
}));

export default function PaySlipDetails() {
  const { t } = useTranslation();
  const navigation = useNavigate();
  const dispatch = useDispatch();

  /* =========================
     Load selected IDs safely
  ========================= */
  const selectedIds = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("selectedPaySlips")) || [];
    } catch {
      return [];
    }
  }, []);

  const { paySlipList, employeePaySlipDetails } = useSelector(payRollSelector);

  const [selectedRows, setSelectedRows] = useState([]);
  const [isSingle, setIsSingle] = useState(false);

  /* =========================
     Fetch Data
  ========================= */
  useEffect(() => {
    if (!selectedIds.length) return;

    if (selectedIds.length === 1) {
      dispatch(onLoadEmployeePaySlipDetails(selectedIds[0]));
      setIsSingle(true);
    } else {
      dispatch(onGetPaySlipList(selectedIds));
      setIsSingle(false);
    }
  }, [selectedIds, dispatch]);

  /* =========================
     Sync Redux Data
  ========================= */
  useEffect(() => {
    if (isSingle && employeePaySlipDetails) {
      setSelectedRows([employeePaySlipDetails]);
    }

    if (!isSingle && paySlipList.length > 0) {
      setSelectedRows(paySlipList);
    }
  }, [isSingle, employeePaySlipDetails, paySlipList]);

  /* =========================
     Print Handler
  ========================= */
    const printRef = useRef();
  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: "Bulk Print Document",
  });

  /* =========================
     Render
  ========================= */
  return (
    <div>
      {/* Header */}
      <div
        className="container d-flex justify-content-between p-3 sticky-top bg-white"
        style={{ top: "8%", zIndex: 1000 }}>
        <div className="d-flex align-items-center">
          <i
            className="fa fa-arrow-left btn"
            onClick={() => navigation(-1)}></i>
          <h5 className="fw-bold mb-0">Pay Slip Details</h5>
        </div>

        <Button
          color="primary"
          onClick={handlePrint}
          disabled={!selectedRows.length}>
          <i className="fa fa-print me-2"></i>
          {t("print_all")}
        </Button>
      </div>

      {/* Slips */}
      <div ref={printRef}>
        {selectedRows.length > 0 ?
          selectedRows.map((row, index) => (
            <SlipWrapper key={row?.id || index} index={index} data={row} />
          ))
        : <div className="text-center p-5">
            <i className="fa-solid fa-spinner fa-spin fs-3"></i>
            <p className="mt-2">{t("loading")}</p>
          </div>
        }
      </div>
    </div>
  );
}

/* =========================
   Slip Wrapper Component
========================= */
const SlipWrapper = ({ index, data }) => {
  return (
    <>
      <div className="d-flex py-2 px-5 align-items-center gap-3 d-print-none">
        <b>Slip {index + 1}</b>
        <div className="bg-black flex-grow-1" style={{ height: "3px" }} />
      </div>

      <PrintSlipTemplate data={data} />
    </>
  );
};
